import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const DocumentationSidebar = ({ isCollapsed, onToggle }) => {
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState({
    'api-basics': true,
    'authentication': false,
    'endpoints': false,
    'best-practices': false,
    'glossary': false
  });

  const documentationStructure = [
    {
      id: 'api-basics',
      label: 'API Basics',
      icon: 'BookOpen',
      children: [
        { id: 'introduction', label: 'Introduction', anchor: '#introduction' },
        { id: 'getting-started', label: 'Getting Started', anchor: '#getting-started' },
        { id: 'base-url', label: 'Base URL & Endpoints', anchor: '#base-url' },
        { id: 'http-methods', label: 'HTTP Methods', anchor: '#http-methods' },
        { id: 'response-format', label: 'Response Format', anchor: '#response-format' },
        { id: 'rate-limiting', label: 'Rate Limiting', anchor: '#rate-limiting' },
        { id: 'status-codes', label: 'HTTP Status Codes', anchor: '#status-codes' }
      ]
    },
    {
      id: 'authentication',
      label: 'Authentication',
      icon: 'Shield',
      children: [
        { id: 'auth-overview', label: 'Authentication Overview', anchor: '#auth-overview' },
        { id: 'api-keys', label: 'API Key Authentication', anchor: '#api-keys' },
        { id: 'oauth', label: 'OAuth 2.0 Flow', anchor: '#oauth' },
        { id: 'jwt-tokens', label: 'JWT Token Management', anchor: '#jwt-tokens' },
        { id: 'security-practices', label: 'Security Best Practices', anchor: '#security-practices' }
      ]
    },
    {
      id: 'endpoints',
      label: 'API Endpoints',
      icon: 'Globe',
      children: [
        { id: 'products-api', label: 'Products API', anchor: '#products-api' },
        { id: 'brands-api', label: 'Brands API', anchor: '#brands-api' },
        { id: 'categories-api', label: 'Categories API', anchor: '#categories-api' },
        { id: 'pincode-api', label: 'Pincode Serviceable API', anchor: '#pincode-api' },
        { id: 'stock-api', label: 'Stock Availability API', anchor: '#stock-api' },
        { id: 'orders-api', label: 'Orders API', anchor: '#orders-api' },
        { id: 'invoice-api', label: 'Invoice API', anchor: '#invoice-api' },
        { id: 'webhooks-api', label: 'Webhooks API', anchor: '#webhooks-api' }
      ]
    },
    {
      id: 'best-practices',
      label: 'Best Practices',
      icon: 'CheckCircle',
      children: [
        { id: 'error-handling', label: 'Error Handling', anchor: '#error-handling' },
        { id: 'pagination', label: 'Pagination Guidelines', anchor: '#pagination' },
        { id: 'filtering-sorting', label: 'Filtering & Sorting', anchor: '#filtering-sorting' },
        { id: 'versioning', label: 'API Versioning', anchor: '#versioning' },
        { id: 'testing', label: 'Testing Your Integration', anchor: '#testing' }
      ]
    },
    {
      id: 'glossary',
      label: 'Glossary',
      icon: 'BookText',
      children: [
        { id: 'terms-definitions', label: 'Terms & Definitions', anchor: '#terms-definitions' },
        { id: 'common-errors', label: 'Common Error Codes', anchor: '#common-errors' },
        { id: 'troubleshooting', label: 'Troubleshooting Guide', anchor: '#troubleshooting' }
      ]
    }
  ];

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev?.[sectionId]
    }));
  };

  const handleAnchorClick = (anchor) => {
    const element = document.querySelector(anchor);
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className={`
      bg-card border-r border-border transition-all duration-300
      ${isCollapsed ? 'w-0 overflow-hidden' : 'w-full'}
    `}>
      <div className="h-full overflow-y-auto">
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">Documentation</h3>
            <button
              onClick={onToggle}
              className="lg:hidden p-1 rounded-md hover:bg-muted transition-colors"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>

        <nav className="p-2">
          {documentationStructure?.map((section) => (
            <div key={section?.id} className="mb-2">
              <button
                onClick={() => toggleSection(section?.id)}
                className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-foreground hover:bg-muted rounded-lg transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <Icon name={section?.icon} size={16} />
                  <span>{section?.label}</span>
                </div>
                <Icon 
                  name={expandedSections?.[section?.id] ? "ChevronDown" : "ChevronRight"} 
                  size={14} 
                />
              </button>
              
              {expandedSections?.[section?.id] && (
                <div className="ml-6 mt-1 space-y-1">
                  {section?.children?.map((item) => (
                    <button
                      key={item?.id}
                      onClick={() => handleAnchorClick(item?.anchor)}
                      className="block w-full text-left px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-md transition-colors"
                    >
                      {item?.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
      </div>
    </div>
  );
};

export default DocumentationSidebar;